import {
  Animation,
  ArcRotateCamera,
  Color3,
  HemisphericLight,
  Mesh,
  MeshBuilder,
  StandardMaterial,
  Texture,
  Vector3,
} from '@babylonjs/core';
import { Button, Control, TextBlock } from '@babylonjs/gui';
import { Asset, AssetType } from '../Asset';
import { AssetObject } from '../AssetObject';
import { Game } from '../Game';
import { GameState } from '../GameState';
import { AssetObjectConfig, MeshFactory, SkyBoxConfig } from '../MeshFactory';

export class Gui {
  static primaryColor = Color3.FromHexString('#FFFFFF');
  static secondaryColor = Color3.FromHexString('#e48716');
  static accentColor = Color3.FromHexString('#494339ff');
  static backgroundColor = Color3.FromHexString('#000000');

  static backgroundAlpha = 0.75;

  static fontFamily = 'monospace';
  static fontSizeXS = '2%';
  static fontSizeS = '3%';
  static fontSizeM = '4%';
  static fontSizeL = '5%';
  static fontSizeXL = '6%';
  static fontSizeXXL = '8%';
  static fontSizeXXXL = '20%';

  static controlBackground = 'rgba(0, 0, 0, 0.6)';

  static async createBackButton(game: Game, toState: GameState, txt?: string): Promise<Button> {
    if (!txt) {
      txt = '📺';
    }
    const button = game.gui2D.createButton(
      'button_back_' + toState,
      txt,
      () => {
        game.sound.play('click');
        game.routeTo(toState);
      },
      game.gui2D.ui.rootContainer,
      1,
      game.gui2D.sceneButtonColors()
    );

    button.top = '-46%';
    button.left = '47%';
    button.width = '32px';
    button.height = '32px';
    button.fontSize = '32px';
    button.thickness = 0;

    return button;
  }

  static async createPauseButton(game: Game): Promise<Button> {
    const button = game.gui2D.createButton(
      'button_pause',
      '🥯',
      () => {
        game.pause();
      },
      game.gui2D.ui.rootContainer,
      1,
      game.gui2D.sceneButtonColors()
    );

    button.top = '-46%';
    button.left = '42%';
    button.width = '32px';
    button.height = '32px';
    button.fontSize = '32px';
    button.thickness = 0;
    return button;
  }

  static async createDefaultScene(game: Game, skyBox?: SkyBoxConfig, subTitleText?: string) {
    game.scene.activeCamera = new ArcRotateCamera('default_camera', -Math.PI / 2, Math.PI / 2, 10, Vector3.Zero());

    const light = new HemisphericLight('default_light', new Vector3(0.5, 1.0, 0.5), game.scene);

    if (skyBox) {
      skyBox.rotationSpeed = skyBox.rotationSpeed || 0.00001;
      await MeshFactory.createSkyBox(skyBox, 'default_skybox', game.scene);
    }

    const logoPlane = MeshBuilder.CreatePlane('title', { width: 120, height: 20 }, game.scene);
    logoPlane.position = new Vector3(0, 52, 150);
    logoPlane.billboardMode = Mesh.BILLBOARDMODE_ALL;

    const logoMaterial = new StandardMaterial('logoMaterial', game.scene);
    const logoTexture = new Texture('assets/image/logo.png', game.scene);
    logoTexture.hasAlpha = true;
    logoMaterial.diffuseTexture = logoTexture;
    logoMaterial.opacityTexture = logoTexture;
    logoMaterial.emissiveTexture = logoTexture;
    logoMaterial.disableLighting = true;
    logoMaterial.backFaceCulling = false;
    logoPlane.material = logoMaterial;

    const pulseAnimation = new Animation('titlePulse', 'scaling', 30, Animation.ANIMATIONTYPE_VECTOR3, Animation.ANIMATIONLOOPMODE_CYCLE);
    pulseAnimation.setKeys([
      { frame: 0, value: new Vector3(1, 1, 1) },
      { frame: 90, value: new Vector3(0.95, 0.95, 0.95) },
      { frame: 180, value: new Vector3(1, 1, 1) },
    ]);
    logoPlane.animations.push(pulseAnimation);
    game.scene.beginAnimation(logoPlane, 0, 180, true, 2);

    if (subTitleText) {
      const title = Gui.createTitle(subTitleText);
      title.top = '-23%';
      game.gui2D.ui.addControl(title);
    }
  }

  static createTitle(titleText: string): TextBlock {
    const title = new TextBlock('title', titleText);
    title.color = Gui.primaryColor.toHexString();
    title.fontSize = Gui.fontSizeXL;
    title.fontFamily = Gui.fontFamily;
    title.fontWeight = 'bold';
    title.alpha = 0.7;
    title.textVerticalAlignment = Control.VERTICAL_ALIGNMENT_CENTER;
    title.textHorizontalAlignment = Control.HORIZONTAL_ALIGNMENT_CENTER;
    title.outlineWidth = 4;
    title.outlineColor = Gui.secondaryColor.toHexString();
    return title;
  }

  static async createAnimatedPlayer(game: Game, animationName: string, position: Vector3, loop: boolean = false): Promise<Mesh> {
    // Load player asset
    const playerAsset = new Asset('player', AssetType.MESH, 'assets/player/', 'player.glb');
    await game.assetFactory.load([playerAsset]);

    const playerAssetObj = new AssetObject('player').withClone(true);
    const config = AssetObjectConfig.builder()
      .object(playerAssetObj)
      .name('player_mesh')
      .scale(0.01)
      .position(position)
      .rotation(new Vector3(0, Math.PI, 0))
      .build();

    // Mock level for buildMesh (it needs level for game/scene access)
    // Create a temporary object that looks like Level enough for MeshFactory.
    const mockLevel = {
      game: game,
      scene: game.scene,
      addShadowCaster: () => {}, // Mock shadow generator
      highlights: { addMesh: () => {} }, // Mock highlights
      getAtlasByName: () => null,
      assetFactory: game.assetFactory, // crucial
      getMaterial: () => null,
    } as any;

    // Build mesh
    const mesh = config.buildMesh(mockLevel);
    MeshFactory.configureMesh(mesh, config, mockLevel);

    // Find and play animation
    if (animationName) {
      const anim = game.scene.getAnimationGroupByName(animationName);
      anim.enableBlending = true;
      anim.blendingSpeed = 0.1;

      const current = anim.start(loop, 1.0, anim.from, anim.to, false);
      if (!loop) {
        current.onAnimationEndObservable.add(() => {
          const idle = game.scene.getAnimationGroupByName('Idle');
          idle.enableBlending = true;
          idle.blendingSpeed = 0.1;
          if (idle) {
            idle.start(true);
          }
        });
      }
    }
    mesh.lookAt(game.scene.activeCamera.position);
    return mesh;
  }

  static async setDefaultShadow(control: Control) {
    control.shadowColor = Gui.backgroundColor.toHexString();
    control.shadowBlur = 1;
    control.shadowOffsetX = 1;
    control.shadowOffsetY = 1;
  }
}
